import React, { useState, useMemo } from 'react';
import { X, MessageCircle, Check, SkipForward, ChevronLeft } from 'lucide-react';
import { generateMessage } from '../utils/data';

// queue = array of patients already filtered/sorted by the caller (most overdue first)
export default function BulkReminderQueue({ queue, practiceName, onClose, onMarkContacted }) {
  const [index, setIndex] = useState(0);
  const [sentIds, setSentIds] = useState([]);
  const [skippedIds, setSkippedIds] = useState([]);

  const patient = queue[index];
  const done = index >= queue.length;
  const message = useMemo(() => patient ? generateMessage(patient, practiceName) : '', [patient, practiceName]);

  function advance() {
    if (index + 1 >= queue.length) setIndex(queue.length); // triggers done state
    else setIndex(i => i + 1);
  }

  function handleSendAndMark() {
    if (!patient) return;
    const phone = (patient.phone || '').replace(/\D/g, '');
    if (phone) {
      const url = `https://wa.me/${phone}?text=${encodeURIComponent(message)}`;
      window.open(url, '_blank');
    }
    onMarkContacted(patient.id);
    setSentIds(ids => [...ids, patient.id]);
    advance();
  }

  function handleSkip() {
    if (!patient) return;
    setSkippedIds(ids => [...ids, patient.id]);
    advance();
  }

  function handleBack() {
    setIndex(i => Math.max(0, i - 1));
  }

  return (
    <div style={overlayStyle} onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={panelStyle}>
        <div style={headerStyle}>
          <div>
            <div style={{ fontFamily: "'JetBrains Mono', monospace", fontWeight: 700, fontSize: 15, color: '#1B2B4B' }}>
              Today's reminders
            </div>
            <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 12, color: '#7A7A72', marginTop: 2 }}>
              {done ? 'All done' : `Patient ${index + 1} of ${queue.length}`}
            </div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#7A7A72' }}>
            <X size={18} />
          </button>
        </div>

        {/* Progress bar */}
        <div style={progressTrackStyle}>
          <div style={{ ...progressFillStyle, width: `${queue.length ? (Math.min(index, queue.length) / queue.length) * 100 : 0}%` }} />
        </div>

        {!done && patient && (
          <div style={{ padding: '24px 28px', flex: 1, overflowY: 'auto' }}>
            <div style={patientCardStyle}>
              <div style={{ fontFamily: "'JetBrains Mono', monospace", fontWeight: 700, fontSize: 18, color: '#1B2B4B' }}>
                {patient.firstName} {patient.lastName}
              </div>
              <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 12, color: '#7A7A72', marginTop: 4 }}>
                {patient.phone || 'No phone on file'} · {patient.treatmentType}
              </div>
            </div>

            <label style={labelStyle}>Message preview</label>
            <div style={messageBoxStyle}>{message}</div>

            {!patient.phone && (
              <div style={warningBoxStyle}>
                No phone number on file — sending is disabled. Skip or add a number from the patient's profile.
              </div>
            )}
          </div>
        )}

        {done && (
          <div style={{ padding: '40px 28px', flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
            <div style={{ fontFamily: "'JetBrains Mono', monospace", fontWeight: 700, fontSize: 18, color: '#1B2B4B', marginBottom: 8 }}>
              Queue cleared
            </div>
            <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 13, color: '#7A7A72', lineHeight: 1.7 }}>
              {sentIds.length} sent and marked contacted<br />
              {skippedIds.length} skipped
            </div>
          </div>
        )}

        <div style={footerStyle}>
          {!done ? (
            <>
              <button onClick={handleBack} disabled={index === 0} style={backBtnStyle}>
                <ChevronLeft size={14} />
              </button>
              <button onClick={handleSkip} style={skipBtnStyle}>
                <SkipForward size={14} />
                Skip
              </button>
              <button onClick={handleSendAndMark} style={sendBtnStyle} disabled={!patient.phone}>
                <MessageCircle size={14} />
                Send via WhatsApp & mark contacted
              </button>
            </>
          ) : (
            <button onClick={onClose} style={{ ...sendBtnStyle, flex: 1 }}>
              <Check size={14} />
              Close
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

const overlayStyle = { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', zIndex: 1000, display: 'flex', justifyContent: 'flex-end' };
const panelStyle = { background: '#fff', width: 460, maxWidth: '100vw', height: '100vh', display: 'flex', flexDirection: 'column', boxShadow: '-4px 0 24px rgba(0,0,0,0.12)' };
const headerStyle = { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', padding: '20px 28px', borderBottom: '1px solid #E5E5E0' };
const progressTrackStyle = { height: 3, background: '#F0F0EC' };
const progressFillStyle = { height: '100%', background: '#2D9B8A', transition: 'width 0.2s ease' };
const patientCardStyle = { background: '#F0F7F6', border: '1px solid #B5DED9', borderRadius: 2, padding: '14px 16px', marginBottom: 20 };
const labelStyle = { display: 'block', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', color: '#1B2B4B', marginBottom: 8, fontFamily: "'JetBrains Mono', monospace" };
const messageBoxStyle = { background: '#FAFAF8', border: '1px solid #DDDDD8', borderRadius: 2, padding: '16px', fontSize: 13, lineHeight: 1.7, color: '#2A2A25', fontFamily: "'JetBrains Mono', monospace", whiteSpace: 'pre-wrap' };
const warningBoxStyle = { marginTop: 16, padding: '12px 14px', background: '#FEF0EF', border: '1px solid #F5C6C2', borderRadius: 2, fontSize: 12, color: '#C0392B', fontFamily: "'JetBrains Mono', monospace" };
const footerStyle = { display: 'flex', gap: 8, padding: '16px 28px', borderTop: '1px solid #E5E5E0' };
const backBtnStyle = { padding: '10px 12px', background: '#fff', color: '#1B2B4B', border: '1px solid #DDDDD8', borderRadius: 2, cursor: 'pointer', display: 'flex', alignItems: 'center' };
const skipBtnStyle = { display: 'flex', alignItems: 'center', gap: 6, padding: '10px 14px', background: '#fff', color: '#7A7A72', border: '1px solid #DDDDD8', borderRadius: 2, cursor: 'pointer', fontSize: 13, fontWeight: 600, fontFamily: "'JetBrains Mono', monospace" };
const sendBtnStyle = { display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, padding: '10px 14px', background: '#25D366', color: '#fff', border: 'none', borderRadius: 2, cursor: 'pointer', fontSize: 13, fontWeight: 600, fontFamily: "'JetBrains Mono', monospace", flex: 1 };